import { useState, useEffect, useRef } from "react";
import {
  Button,
  Group,
  Text,
  Card,
  LoadingOverlay,
  ActionIcon,
} from "@mantine/core";
import { Carousel } from "@mantine/carousel";
import {
  IconPlayerSkipForward,
  IconChevronRight,
  IconChevronLeft,
} from "@tabler/icons-react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  useNextPendingTask,
  useImageGallery,
  useFetchTask,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { ImageWithBoundingBox } from "@labeling-tools/ui-components/dist/ImageWithBoundingBox";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import {
  setActiveModal,
  setSelectedEmptyArea,
  setSelectedProductProposal,
  setMatches,
  setActiveReferenceImageIndex,
  reset,
} from "../store";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useCompleteJobTask } from "@labeling-tools/api/lebron/react-query-hooks/useCompleteJobTask";
import { useProfileHome } from "@/hooks/useProfileHome";
import { RemainingFacesTooltip } from "./RemainingFacesTooltip";
import { ProgressLostModal } from "./modals/ProgressLostModal";
import { SkipModal } from "./modals/SkipModal";
import { TaskCounter } from "./TaskCounter";

export const OosSelectionContent = ({
  mode,
}: {
  mode?: "admin" | "viewer";
}) => {
  const navigate = useNavigate();
  const { jobsPath } = useProfileHome();
  const dispatch = useAppDispatch();
  const [buttonsEnabled, setButtonsEnabled] = useState(true);
  const [nextBtnLoading, setNextBtnLoading] = useState(false);
  const [embla, setEmbla] = useState<any>(null);
  const [warningCallback, setWarningCallback] = useState<(() => void) | null>(
    null,
  );
  const activeReferenceImageIndex = useAppSelector(
    (state) => state.oos.activeReferenceImageIndex,
  );
  const selectedEmptyArea = useAppSelector(
    (state) => state.oos.selectedEmptyArea,
  );
  const selectedProductProposal = useAppSelector(
    (state) => state.oos.selectedProductProposal,
  );
  const matches = useAppSelector((state) => state.oos.matches);

  const { jobId } = useParams<{ jobId: string }>();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");
  const queryClient = useQueryClient();

  // the fetch hook used depends on wether we're on an admin page or not
  const {
    data: nextPendingTask,
    isLoading: nextPendingTaskLoading,
    isFetching: nextPendingTaskFetching,
    isError: nextPendingTaskError,
  } = mode === "admin"
    ? useFetchTask(jobId, taskId)
    : useNextPendingTask(jobId);
  const {
    mutate: galleryMutation,
    data: gallery,
    isPending: galleryLoading,
  } = useImageGallery();
  const {
    data: referenceImageGallery,
    mutate: referenceImageGalleryMutation,
    isPending: referenceImageGalleryLoading,
  } = useImageGallery();
  const { mutateAsync: completeJobTask } = useCompleteJobTask(
    jobId || "",
    nextPendingTask?.id || "",
  );

  // reset the store when a new task arrives (tracked by task identity, not loading state)
  const prevTaskIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (nextPendingTask?.id && nextPendingTask.id !== prevTaskIdRef.current) {
      prevTaskIdRef.current = nextPendingTask.id;
      dispatch(reset());
    }
  }, [nextPendingTask?.id, dispatch]);

  useEffect(() => {
    if (nextPendingTask?.image_id) {
      galleryMutation({
        image_ids: [nextPendingTask.image_id],
      });
      referenceImageGalleryMutation({
        image_ids:
          (nextPendingTask?.data?.reference_images as any[])?.map(
            (image: any) => image.image_id,
          ) || [],
      });
    }
  }, [nextPendingTask, galleryMutation, referenceImageGalleryMutation]);

  // if nextPendingTask errors (usually all tasks for a job are completed) return null
  useEffect(() => {
    if (nextPendingTaskError) {
      queryClient.setQueryData(["nextPendingTask", jobId], null);
    }
  }, [nextPendingTaskError]);

  const resetLabelingProgress = () => {
    dispatch(setSelectedProductProposal(null));
    dispatch(setMatches([]));
    dispatch(setActiveReferenceImageIndex(0));
  };

  // Custom carousel navigation functions
  const actuallyGoToPrevSlide = () => {
    if (embla) embla.scrollPrev();
    if (activeReferenceImageIndex > 0) {
      dispatch(setActiveReferenceImageIndex(activeReferenceImageIndex - 1));
    } else {
      dispatch(
        setActiveReferenceImageIndex(
          (nextPendingTask?.data?.reference_images as any[])?.length - 1 || 0,
        ),
      );
    }
    if (selectedProductProposal) {
      dispatch(setSelectedProductProposal(null));
    }
  };

  const actuallyGoToNextSlide = () => {
    if (embla) embla.scrollNext();

    // track selected slide index
    if (
      activeReferenceImageIndex <
      ((nextPendingTask?.data?.reference_images as any[])?.length - 1 || 0)
    ) {
      dispatch(setActiveReferenceImageIndex(activeReferenceImageIndex + 1));
    } else {
      dispatch(setActiveReferenceImageIndex(0));
    }
    if (selectedProductProposal) {
      dispatch(setSelectedProductProposal(null));
    }
  };

  const goToPrevSlide = () => {
    // if progress has been made, show warning modal before moving slides
    // if (matches.length > 0) {
    //   setWarningCallback(() => actuallyGoToPrevSlide);
    //   return dispatch(setActiveModal("progressLost"));
    // }

    actuallyGoToPrevSlide();
  };

  const goToNextSlide = () => {
    // if progress has been made, show warning modal before moving slides
    // if (matches.length > 0) {
    //   setWarningCallback(() => actuallyGoToNextSlide);
    //   return dispatch(setActiveModal("progressLost"));
    // }

    actuallyGoToNextSlide();
  };

  const handleConfirmFaces = (hasRemainingFaces: boolean) => {
    console.log("hasRemainingFaces", hasRemainingFaces);
    if (
      typeof selectedEmptyArea === "number" &&
      typeof selectedProductProposal === "number"
    ) {
      dispatch(
        setMatches([
          ...matches,
          {
            empty_area_index: selectedEmptyArea,
            product_proposal_index: selectedProductProposal,
            remaining_faces: hasRemainingFaces,
            reference_image_index: activeReferenceImageIndex,
          },
        ]),
      );
      dispatch(setSelectedEmptyArea(null));
      dispatch(setSelectedProductProposal(null));
    }
  };

  // get image urls from gallery
  const emptyAreaImages = gallery?.images.map((image) => image.image);
  const missingItemImageUrl = emptyAreaImages?.[0];
  const emptyAreas = nextPendingTask?.data?.empty_areas as any[] | undefined;

  const handleNext = async () => {
    setNextBtnLoading(true);
    setButtonsEnabled(false);
    // Handle next logic
    console.log("Completed task");
    try {
      await completeJobTask({
        result: {
          reference_image_index: activeReferenceImageIndex, // TODO: maybe remove
          matches,
        },
      });
      queryClient.invalidateQueries({ queryKey: ["nextPendingTask", jobId] });
    } catch (error) {
      console.error(error);
    } finally {
      setNextBtnLoading(false);
      setButtonsEnabled(true);
    }
  };

  if (
    nextPendingTaskLoading ||
    galleryLoading ||
    nextPendingTaskFetching ||
    referenceImageGalleryLoading
  )
    return (
      <div className="flex justify-center items-center">
        <LoadingOverlay visible={true} />
      </div>
    );
  const noGallery =
    !galleryLoading &&
    !gallery &&
    !referenceImageGalleryLoading &&
    !referenceImageGallery;
  const noImagesFound =
    !missingItemImageUrl ||
    !referenceImageGallery?.images?.length ||
    !nextPendingTask ||
    !referenceImageGallery;
  if (noGallery || noImagesFound) {
    return (
      <div className="flex flex-col justify-center items-center pt-10">
        <div className="mb-4 text-xl">No task found</div>
        <div>
          <Button
            onClick={() => {
              navigate(jobsPath);
            }}
          >
            Go to dashboard
          </Button>
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col min-h-screen bg-gray-50">
      {/* Main Content */}
      <div className="flex-1 flex flex-col items-center p-6 pb-24">
        <Card
          shadow="sm"
          padding="lg"
          radius="md"
          withBorder
          className="w-full max-w-4xl"
        >
          <div className="flex flex-col items-center mb-8 relative">
            <Text size="xl" fw={600} className="text-gray-900">
              Pick the Correct Missing Items
            </Text>
            {mode !== "admin" && <TaskCounter taskId={nextPendingTask?.id} />}
          </div>

          <Group grow className="mb-8" align="flex-start">
            {/* Missing Item Section */}
            <div className="flex flex-col items-center">
              <Text size="lg" fw={500} className="mb-4 text-gray-700">
                Missing Item
              </Text>
              {missingItemImageUrl && (
                <ImageWithBoundingBox
                  src={missingItemImageUrl}
                  alt="Found Item"
                  points={emptyAreas ?? []}
                  variant="empty"
                  clickable={mode !== "admin"}
                  selectedBoxes={
                    selectedEmptyArea !== null
                      ? [
                          ...(matches?.map(
                            (result) => result.empty_area_index,
                          ) || []),
                          selectedEmptyArea,
                        ]
                      : [
                          ...(matches?.map(
                            (result) => result.empty_area_index,
                          ) || []),
                        ]
                  }
                  onBoxClick={(boxIndex) => {
                    console.log("boxIndex", boxIndex);
                    const existingMatch = matches.find(
                      (match) => match.empty_area_index === boxIndex,
                    );
                    if (selectedEmptyArea === boxIndex) {
                      dispatch(setSelectedEmptyArea(null));
                      dispatch(setSelectedProductProposal(null));
                    } else if (existingMatch) {
                      dispatch(setSelectedEmptyArea(null));
                      dispatch(setSelectedProductProposal(null));
                      dispatch(
                        setMatches(
                          matches.filter(
                            (match) => match.empty_area_index !== boxIndex,
                          ),
                        ),
                      );
                    } else {
                      dispatch(setSelectedEmptyArea(boxIndex));
                    }
                  }}
                  extraBoxContent={null}
                  matches={
                    matches?.map((match) => match.empty_area_index) || []
                  }
                  crop
                />
              )}
            </div>

            {/* Reference Section */}
            <div className="flex flex-col items-center">
              <Text size="lg" fw={500} className="mb-4 text-gray-700">
                Reference
              </Text>
              <div className="relative w-full">
                {/* Custom Previous Button - styled like Mantine carousel controls */}
                <ActionIcon
                  variant="filled"
                  color="white"
                  size="lg"
                  className="absolute left-2 z-20"
                  onClick={goToPrevSlide}
                  style={{
                    top: "50%",
                    position: "absolute",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    borderRadius: "50%",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    color: "#000",
                  }}
                >
                  <IconChevronLeft size={18} />
                </ActionIcon>

                {/* Custom Next Button - styled like Mantine carousel controls */}
                <ActionIcon
                  variant="filled"
                  color="white"
                  size="lg"
                  className="absolute right-2 z-20"
                  onClick={goToNextSlide}
                  style={{
                    top: "50%",
                    position: "absolute",
                    transform: "translateY(-50%)",
                    backgroundColor: "rgba(255, 255, 255, 0.7)",
                    borderRadius: "50%",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    border: "1px solid rgba(0, 0, 0, 0.1)",
                    color: "#000",
                  }}
                >
                  <IconChevronRight size={18} />
                </ActionIcon>

                <Carousel
                  withIndicators
                  withControls={false}
                  draggable={false}
                  getEmblaApi={setEmbla}
                  // height={500}
                  slideSize="100%"
                  align="center"
                  loop
                >
                  {(nextPendingTask?.data?.reference_images as any[])?.map(
                    (referenceImage: any, index: number) => {
                      const matchingGalleryImage =
                        referenceImageGallery?.images?.find(
                          (image) => image.id === referenceImage.image_id,
                        );
                      return (
                        <Carousel.Slide key={index}>
                          <ImageWithBoundingBox
                            src={matchingGalleryImage?.image ?? ""}
                            alt="Found Item"
                            points={
                              referenceImage.product_proposal_detections ?? []
                            }
                            variant="proposal"
                            modelPredictions={Object.values(
                              referenceImage.empty_area_to_detection_mapping,
                            )}
                            selectedBoxes={
                              selectedProductProposal !== null
                                ? [
                                    ...(matches
                                      ?.filter(
                                        (match) =>
                                          match.reference_image_index ===
                                          activeReferenceImageIndex,
                                      )
                                      ?.map(
                                        (result) =>
                                          result.product_proposal_index,
                                      ) || []),
                                    selectedProductProposal,
                                  ]
                                : [
                                    ...(matches
                                      ?.filter(
                                        (match) =>
                                          match.reference_image_index ===
                                          activeReferenceImageIndex,
                                      )
                                      ?.map(
                                        (result) =>
                                          result.product_proposal_index,
                                      ) || []),
                                  ]
                            }
                            activeBox={selectedProductProposal}
                            onBoxClick={(boxIndex) => {
                              console.log("boxIndex", boxIndex);
                              if (selectedProductProposal === boxIndex) {
                                dispatch(setSelectedProductProposal(null));
                              } else if (!selectedProductProposal) {
                                dispatch(setSelectedProductProposal(boxIndex));
                              }
                            }}
                            extraBoxContent={
                              activeReferenceImageIndex === index ? (
                                <RemainingFacesTooltip
                                  onConfirmFaces={handleConfirmFaces}
                                />
                              ) : null
                            }
                            clickable={
                              selectedEmptyArea !== null &&
                              activeReferenceImageIndex === index &&
                              mode !== "admin"
                            }
                            matches={
                              matches?.map(
                                (match) => match.product_proposal_index,
                              ) || []
                            }
                            matchReferenceIndexes={
                              matches?.map(
                                (match) => match.reference_image_index ?? 0,
                              ) || []
                            }
                            referenceImageIndex={index}
                            crop
                          />
                        </Carousel.Slide>
                      );
                    },
                  )}
                </Carousel>
              </div>
            </div>
          </Group>
        </Card>
      </div>

      {/* Sticky Bottom Navigation */}
      {mode !== "admin" && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
          <div className="flex justify-center p-4">
            <div className="w-full max-w-4xl">
              <Group justify="center" gap="xl">
                <Button
                  variant="outline"
                  color="gray"
                  size="md"
                  leftSection={<IconPlayerSkipForward size={20} />}
                  onClick={() => dispatch(setActiveModal("skip"))}
                  className="min-w-[120px]"
                  disabled={!buttonsEnabled}
                >
                  Skip
                </Button>

                <Button
                  variant="filled"
                  color="blue"
                  size="md"
                  onClick={handleNext}
                  className="min-w-[120px]"
                  loading={nextBtnLoading}
                  disabled={!buttonsEnabled}
                >
                  Next
                </Button>
              </Group>
            </div>
          </div>
        </div>
      )}

      {/* Progress lost modal */}
      <ProgressLostModal
        onConfirm={() => {
          resetLabelingProgress();
          if (warningCallback) {
            warningCallback();
            setWarningCallback(null);
          }
        }}
      />

      {/* Skip modal */}
      {nextPendingTask?.id && <SkipModal task_id={nextPendingTask?.id} />}
    </div>
  );
};
